import { useEffect, useState } from 'react';

/**
 * Which of the three navigation structures the viewport is wide enough for.
 *
 * - `drawer`  (<= 768) an off-canvas panel behind a 56 px top bar
 * - `rail`    (769 - 1279) an 80 px LABELLED rail with click flyouts
 * - `sidebar` (>= 1280) the full 216 px grouped list
 *
 * These are three different DOM shapes, not three widths of one shape — a
 * flyout sub-menu and an inline sub-list cannot be the same markup — so the
 * choice is made in JS rather than with a `hidden`/`block` pair that would
 * ship both trees to every viewport and double the accessible names.
 */
export type ShellMode = 'drawer' | 'rail' | 'sidebar';

const RAIL_MIN = '(min-width: 769px)';
const SIDEBAR_MIN = '(min-width: 1280px)';

function currentMode(): ShellMode {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    // No media-query support (a test environment, a server render): assume the
    // full sidebar. It is the mode with every label visible, so nothing is
    // hidden from a reader that cannot ask about width.
    return 'sidebar';
  }
  if (window.matchMedia(SIDEBAR_MIN).matches) return 'sidebar';
  if (window.matchMedia(RAIL_MIN).matches) return 'rail';
  return 'drawer';
}

export function useShellMode(override?: ShellMode): ShellMode {
  const [mode, setMode] = useState<ShellMode>(() => override ?? currentMode());

  useEffect(() => {
    if (override !== undefined) {
      setMode(override);
      return;
    }
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    const queries = [window.matchMedia(RAIL_MIN), window.matchMedia(SIDEBAR_MIN)];
    const update = (): void => {
      setMode(currentMode());
    };
    update();
    for (const query of queries) query.addEventListener('change', update);
    return () => {
      for (const query of queries) query.removeEventListener('change', update);
    };
  }, [override]);

  return mode;
}
